import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/hooks/useApi';

// ============================================================================
// Types
// ============================================================================

export type Rating = 'up' | 'down';

export interface FeedbackResponse {
  userRating: Rating | null;
}

// ============================================================================
// Query Keys
// ============================================================================

export const feedbackKeys = {
  all: ['feedback'] as const,
  node: (nodeId: string) => [...feedbackKeys.all, 'node', nodeId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch user's feedback for a node
 */
export function useNodeFeedback(nodeId: string | undefined) {
  return useQuery({
    queryKey: feedbackKeys.node(nodeId ?? ''),
    queryFn: async () => {
      const response = await api.get<FeedbackResponse>(`/nodes/${nodeId}/feedback`);
      return response.data;
    },
    enabled: !!nodeId,
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Submit feedback for a node
 */
export function useSubmitNodeFeedback(nodeId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rating: Rating) => {
      if (!nodeId) {
        throw new Error('nodeId is required to submit feedback');
      }
      const response = await api.post<FeedbackResponse>(`/nodes/${nodeId}/feedback`, { rating });
      return response.data;
    },
    onSuccess: (data) => {
      // Update the cached feedback
      if (nodeId) {
        queryClient.setQueryData(feedbackKeys.node(nodeId), data);
      }
    },
  });
}
